"use client"

import React from 'react'
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { useCodeValues } from "@/hooks/use-code-values"
import { Transaction } from "@/hooks/use-transaction-reports"
import { cn } from "@/lib/utils"
import { useFormatters } from "@/hooks/use-formatters"
import { FileText } from "lucide-react"
import { ParentNavigationLink } from '../ui/parent-navigation-link'
import { SortIcon, getAriaSortValue } from '@/components/ui/sort-icon'
import { TRANSACTION_STATUS } from '@/lib/constants/transaction-codes'

interface TransactionsContentProps {
  transactions: Transaction[]
  loading?: boolean
  error?: string | null
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (field: string) => void
}

// Helper function to get status badge className based on status ID
const getStatusClassName = (statusId: number): string => {
  switch (statusId) {
    case TRANSACTION_STATUS.ACTIVE:
      return "bg-green-100 text-green-800 hover:bg-green-100"
    case TRANSACTION_STATUS.PENDING_REVIEW:
      return "bg-blue-100 text-blue-800 hover:bg-blue-100"
    case TRANSACTION_STATUS.DELETED:
    case TRANSACTION_STATUS.REVERSED:
      return "bg-red-100 text-red-800 hover:bg-red-100"
    case TRANSACTION_STATUS.DRAFT:
      return "bg-orange-100 text-orange-800 hover:bg-orange-100"
    case TRANSACTION_STATUS.NEW:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100"
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100"
  }
}

export function TransactionsContent({
  transactions,
  loading = false,
  error,
  sortField,
  sortDirection,
  onSort,
}: TransactionsContentProps) {
  const { getCodeValueText } = useCodeValues()
  const { formatCurrency, formatDate } = useFormatters()

  const renderSortIcon = (field: string) => (
    <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-slate-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span>Loading transactions...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <p>Error loading transactions: {error}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="border-b border-slate-200 bg-slate-50/50">
            <TableHead
              className="font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('TransactionId', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('TransactionId')}>
                ID {renderSortIcon('TransactionId')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('TransactionNumber', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('TransactionNumber')}>
                Transaction # {renderSortIcon('TransactionNumber')}
              </button>
            </TableHead>
            <TableHead className="font-semibold text-slate-700">Status</TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('TransactionTypeId', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('TransactionTypeId')}>
                Type {renderSortIcon('TransactionTypeId')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('TransactionDate', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('TransactionDate')}>
                Date {renderSortIcon('TransactionDate')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('WorkerName', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('WorkerName')}>
                Worker Name {renderSortIcon('WorkerName')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('PayeeName', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('PayeeName')}>
                Payee Name {renderSortIcon('PayeeName')}
              </button>
            </TableHead>
            <TableHead className="font-semibold text-slate-700 whitespace-nowrap">Alternate Bill</TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('FromDate', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('FromDate')}>
                From {renderSortIcon('FromDate')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('ToDate', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('ToDate')}>
                To {renderSortIcon('ToDate')}
              </button>
            </TableHead>
            <TableHead
              className="text-right font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('BillSubtotal', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center justify-end w-full hover:text-slate-900" onClick={() => onSort?.('BillSubtotal')}>
                Gross Bill {renderSortIcon('BillSubtotal')}
              </button>
            </TableHead>
            <TableHead
              className="text-right font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('PaymentSubtotal', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center justify-end w-full hover:text-slate-900" onClick={() => onSort?.('PaymentSubtotal')}>
                Gross/Reported Pay {renderSortIcon('PaymentSubtotal')}
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={100} className="text-center py-12">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="text-slate-500">No transactions found</div>
                  <p className="text-sm text-slate-400">There are no transactions available for this engagement</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            transactions.map((transaction, index) => {
              return (
                <TableRow
                  key={transaction.TransactionId}
                  className={cn(
                    "border-b border-slate-100 hover:bg-blue-50/50 transition-colors",
                    index % 2 === 0 ? "bg-white" : "bg-slate-50/30",
                  )}
                >
                  <TableCell className="font-mono text-sm text-slate-600">
                    {transaction.TransactionId}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    <ParentNavigationLink route={`/next/transaction/${transaction.TransactionId}/summary`}>
                      {transaction.TransactionNumber}
                    </ParentNavigationLink>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("font-medium whitespace-nowrap", getStatusClassName(transaction.TransactionHeaderStatusId))}>
                      {getCodeValueText(transaction.TransactionHeaderStatusId, 'trn.CodeTransactionHeaderStatus')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {getCodeValueText(transaction.TransactionTypeId, 'trn.CodeTransactionType')}
                  </TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap">
                    {formatDate(transaction.TransactionDate, '-')}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {transaction.WorkerName}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {transaction.PayeeName}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {transaction.AlternateBillingClient || '-'}
                  </TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap">
                    {formatDate(transaction.FromDate, '-')}
                  </TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap">
                    {formatDate(transaction.ToDate, '-')}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-slate-900">
                    {formatCurrency(transaction.BillSubtotal)}
                  </TableCell>
                  <TableCell className="text-right text-slate-700">
                    {formatCurrency(transaction.PaymentSubtotal)}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
